// src/pages/OurAuthorsPage.jsx
// Public "Our Authors" page - displays authors with public titles
import React, { useState, useEffect } from 'react';
import { Users, User, BookOpen } from 'lucide-react';
import VisitorNav from '../components/VisitorNav';
import VisitorTopMenuBar from '../components/VisitorTopMenuBar';
import VisitorFooter from '../components/VisitorFooter';
import AboutAuthorModal from '../components/AboutAuthorModal';
import AboutBookModal from '../components/AboutBookModal';
import { BACKEND_URL as backendUrl } from '@/config/api';
import { Seo, PAGE_SEO } from '../components/Seo';

const OurAuthorsPage = () => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [selectedAuthorId, setSelectedAuthorId] = useState(null);
  const [selectedAuthorType, setSelectedAuthorType] = useState("managed");
  
  // Book modal state (when clicking on author's titles)
  const [showBookModal, setShowBookModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${backendUrl}/api/public/authors`);
      
      if (response.ok) {
        const data = await response.json();
        setAuthors(data.authors || []);
      } else {
        setError('Failed to load authors');
      }
    } catch (err) {
      console.error('Error fetching authors:', err);
      setError('Failed to load authors. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getHeadshotUrl = (author) => {
    if (author.image_url) {
      return `${backendUrl}${author.image_url}?t=${Date.now()}`;
    }
    return null;
  };

  const handleAboutAuthorClick = (authorId, authorType) => {
    setSelectedAuthorId(authorId);
    setSelectedAuthorType(authorType || "managed");
    setShowAuthorModal(true);
  };

  const handleSelectBookFromAuthor = (book) => {
    console.log('[OurAuthorsPage] Book selected from author titles:', book.title);
    setShowAuthorModal(false);
    setSelectedAuthorId(null);
    setSelectedBook(book);
    setShowBookModal(true);
  };

  return (
    <>
      <Seo {...PAGE_SEO.authors} />

      <VisitorNav />
      <VisitorTopMenuBar />

      <main className="flex-1 py-12 sm:py-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          {/* Header */}
          <div className="text-center mb-10 sm:mb-14">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-600/20 border border-indigo-500/30 mb-6">
              <Users className="w-8 h-8 text-indigo-400" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
              Our Authors
            </h1>
            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto">
              Meet the authors whose books you can listen to and talk to on NewAudia.
            </p>
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-indigo-500 border-r-transparent border-b-purple-500 border-l-transparent"></div>
              <p className="mt-4 text-slate-400">Loading authors...</p>
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-700/50 bg-red-900/20 p-12 text-center">
              <p className="text-red-400">{error}</p>
              <button
                onClick={fetchAuthors}
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-500 px-6 py-2.5 text-sm font-semibold text-white transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : authors.length === 0 ? (
            <div className="rounded-2xl border border-slate-700 bg-slate-900/50 p-12 text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-indigo-600/20 flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-indigo-400" />
              </div>
              <h2 className="text-xl font-semibold text-slate-200 mb-2">Authors Coming Soon</h2>
              <p className="text-slate-400 max-w-md mx-auto">
                We're adding authors to our platform. Check back soon to discover new voices and stories.
              </p>
            </div>
          ) : (
            /* Authors Grid */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {authors.map((author) => {
                const headshotUrl = getHeadshotUrl(author);
                
                return (
                  <div
                    key={author.id}
                    className="rounded-2xl border border-slate-700 bg-slate-900/50 p-7 transition-colors hover:border-slate-600"
                  >
                    <div className="flex items-start gap-6">
                      {/* Headshot / Fallback */}
                      <div className="flex-shrink-0">
                        {headshotUrl ? (
                          <img
                            src={headshotUrl}
                            alt={author.pen_name}
                            className="w-24 h-24 rounded-xl object-cover border border-slate-600"
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-xl bg-slate-800 border border-slate-600 flex items-center justify-center">
                            <User className="w-12 h-12 text-slate-500" />
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-xl text-slate-100 truncate">
                          {author.pen_name}
                        </h3>
                        
                        {/* Title Count */}
                        <div className="mt-1.5">
                          <span className="inline-flex items-center gap-1.5 text-sm text-slate-300">
                            <BookOpen className="w-4 h-4 text-slate-400" />
                            {author.title_count || 0} {author.title_count === 1 ? 'Title' : 'Titles'}
                          </span>
                        </div>
                        
                        {/* Status - Public friendly */}
                        <div className="mt-2">
                          <span className="inline-flex items-center gap-1.5 text-sm text-indigo-400">
                            Available on NewAudia
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Action Button */}
                    <div className="mt-5">
                      <button
                        onClick={() => handleAboutAuthorClick(author.id, author.author_type)}
                        className="w-full flex items-center justify-center gap-2 rounded-lg bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/30 px-5 py-3 text-base text-indigo-300 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        About the Author
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <VisitorFooter />

      {/* About Author Modal */}
      <AboutAuthorModal
        isOpen={showAuthorModal}
        onClose={() => {
          setShowAuthorModal(false);
          setSelectedAuthorId(null);
          setSelectedAuthorType("managed");
        }}
        authorId={selectedAuthorId}
        authorType={selectedAuthorType}
        onSelectBook={handleSelectBookFromAuthor}
      />

      {/* About Book Modal - For books selected from author's titles */}
      <AboutBookModal
        isOpen={showBookModal}
        onClose={() => {
          setShowBookModal(false);
          setSelectedBook(null);
        }}
        book={selectedBook}
      />
    </>
  );
};

export default OurAuthorsPage;
